// src/components/ServicesPage/ServicesPage.jsx
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./ServicesPage.css";
import { useLocalContext } from "../../context/LocalContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPalette,
  faCode,
  faMagnifyingGlass,
  faShareNodes,
  faBullhorn,
  faLocationDot,
  faArrowLeft,
  faArrowRight,
  faPhone,
  faEnvelope,
  faClock,
} from "@fortawesome/free-solid-svg-icons";

const iconMap = {
  "website-designing": faPalette,
  "web-development": faCode,
  "search-engine-optimization": faMagnifyingGlass,
  "social-media-marketing": faShareNodes,
  "search-engine-marketing": faBullhorn,
  "local-seo": faLocationDot,
};

const ServicesPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { webinfo = {} } = useLocalContext();

  const brandName = webinfo?.name || "Our team";

  // ✅ IDs MUST match your Services grid
  const serviceData = useMemo(
    () => [
      {
        id: "ui-ux-design",
        kicker: "Design",
        title: "Website Designing",
        description:
          "Modern UI that builds trust and turns visitors into inquiries—optimized for every screen size.",
        details:
          "Design is not decoration—it’s direction. We create clean, modern website designs that clearly communicate your value, guide users to take action, and look great across all devices. From layout and typography to section hierarchy and conversion elements, we design pages that feel premium and perform.",
        features: [
          "Homepage + inner page UI design with clear hierarchy.",
          "Mobile-first layouts that stay readable on all screens.",
          "Conversion-focused sections: CTAs, proof, benefits, FAQs.",
          "Brand alignment: colors, typography, spacing, and tone.",
          "Wireframes + final UI (or direct UI, based on your workflow).",
          "Handoff support for developers (Figma + design notes).",
        ],
        outcomes: ["Cleaner messaging", "Higher trust", "Better conversions"],
        testimonials: [
          {
            name: "Ayesha Khan",
            feedback:
              "The new design instantly made our website look premium. Users now understand our offer in seconds.",
          },
          {
            name: "Ethan Brooks",
            feedback:
              "Our bounce rate dropped and inquiries increased. The layout feels clean and purposeful.",
          },
        ],
        faqs: [
          {
            question: "Do you redesign existing websites too?",
            answer:
              "Yes. We can refresh your current site or rebuild page-by-page while keeping your content and brand direction intact.",
          },
          {
            question: "Do you provide Figma files?",
            answer:
              "Yes—Figma is our default. We also provide export assets and developer notes as needed.",
          },
          {
            question: "Is the design conversion-focused or just visual?",
            answer:
              "Both. Every layout decision supports clarity, trust, and conversions—not just aesthetics.",
          },
        ],
      },

      {
        id: "web-development",
        kicker: "Build",
        title: "Web Development",
        description:
          "Fast, secure websites built with clean code—scalable foundations for growth.",
        details:
          "We build websites and web experiences that are fast, responsive, and easy to maintain. Whether it’s WordPress, a custom HTML build, or a modern React/Next.js website, we focus on performance, structure, and long-term scalability—so your site doesn’t break when your business grows.",
        features: [
          "WordPress development (custom themes/sections + optimization).",
          "React/Next.js or HTML/CSS builds as per project needs.",
          "Performance optimization (Core Web Vitals focus).",
          "SEO-friendly structure and accessibility-friendly UI.",
          "Integrations: forms, WhatsApp, CRM, email tools, analytics.",
          "Security basics + clean deployment workflow.",
        ],
        outcomes: ["Faster load times", "Better UX", "Stable foundation"],
        testimonials: [
          {
            name: "Rohit Mehta",
            feedback:
              "The site is noticeably faster and cleaner. Updates are easy and everything feels stable.",
          },
          {
            name: "Sophie Laurent",
            feedback:
              "They delivered exactly what we needed with great attention to performance and details.",
          },
        ],
        faqs: [
          {
            question: "Which platform should we choose?",
            answer:
              "We’ll recommend based on your needs—WordPress for fast content updates, or custom builds for advanced performance and flexibility.",
          },
          {
            question: "Do you handle hosting/deployment?",
            answer:
              "Yes. We can set up deployment, domains, SSL, performance caching, and basic monitoring.",
          },
          {
            question: "Can you maintain the website after launch?",
            answer:
              "Yes—on a monthly plan or as-needed support, depending on your preference.",
          },
        ],
      },

      {
        id: "search-engine-optimization",
        kicker: "Organic",
        title: "Search Engine Optimization",
        description:
          "Grow organic visibility with technical fixes + content strategy built around buyer intent.",
        details:
          "We approach SEO as a long-term growth channel. First we fix technical issues (speed, indexability, structure), then we build a content plan around high-intent keywords your buyers actually search. The goal is steady, compounding traffic that converts into leads and sales—not just rankings.",
        features: [
          "Technical SEO audit + fixes (crawl, index, speed).",
          "Keyword research focused on intent and conversions.",
          "On-page optimization: headings, meta, internal links.",
          "Content plan: pages + blogs mapped to funnel stages.",
          "Local/Global SEO approach based on your market.",
          "Monthly reporting on rankings, traffic, and conversions.",
        ],
        outcomes: ["More visibility", "Qualified traffic", "Compounding growth"],
        testimonials: [
          {
            name: "James Whitaker",
            feedback:
              "We finally got a real SEO plan—not random posts. Organic leads are now predictable.",
          },
          {
            name: "Ananya Roy",
            feedback:
              "The technical fixes alone made a huge difference. Pages started ranking faster and better.",
          },
        ],
        faqs: [
          {
            question: "How quickly will we see results?",
            answer:
              "Some quick wins can show up in weeks, but consistent growth typically compounds over 2–4+ months depending on competition.",
          },
          {
            question: "Do you write content too?",
            answer:
              "We can write content or collaborate with your writers using briefs, outlines, and SEO optimization.",
          },
          {
            question: "Will SEO work globally too?",
            answer:
              "Yes. We can structure SEO for global targeting, specific countries, or multilingual expansion depending on your goals.",
          },
        ],
      },

      {
        id: "social-media-marketing",
        kicker: "Social",
        title: "Social Media Marketing",
        description:
          "Build brand visibility and engagement with content + campaigns that drive inquiries.",
        details:
          "Social media is where trust is built at scale. We help you show up consistently with strong content direction, creative formats, and campaign support—so your audience grows and your brand stays top-of-mind. The output is not just posts—it’s a system for attention and inbound leads.",
        features: [
          "Content strategy + monthly content plan.",
          "Creative direction for posts, reels, carousels, stories.",
          "Copywriting aligned to your brand voice and positioning.",
          "Engagement improvements (hooks, CTAs, structure).",
          "Optional paid boosts for reach and lead generation.",
          "Performance tracking (reach, engagement, inquiries).",
        ],
        outcomes: ["Better consistency", "More engagement", "Inbound leads"],
        testimonials: [
          {
            name: "Neha Sharma",
            feedback:
              "Our content looks consistent and professional now. Engagement improved and inquiries started coming in.",
          },
          {
            name: "Michael Reed",
            feedback:
              "They built us a content system that’s easy to maintain and actually drives business outcomes.",
          },
        ],
        faqs: [
          {
            question: "Which platforms do you support?",
            answer:
              "We commonly work with Instagram, Facebook, LinkedIn, YouTube Shorts, and X—depending on where your audience is.",
          },
          {
            question: "Do you also run ads on social?",
            answer:
              "Yes. If needed, we combine organic content with performance campaigns for predictable lead flow.",
          },
          {
            question: "Do you provide content design too?",
            answer:
              "Yes. We can handle creatives end-to-end or collaborate with your internal design team.",
          },
        ],
      },

      {
        id: "search-engine-marketing",
        kicker: "Paid",
        title: "Search Engine Marketing",
        description:
          "Run paid campaigns that bring qualified leads fast—optimized for CPL and ROAS.",
        details:
          "SEM is the fastest way to generate demand—if it’s executed correctly. We set up and optimize paid campaigns with clear conversion tracking, high-intent targeting, and landing page alignment. Our focus is simple: better leads at a lower cost, with full transparency.",
        features: [
          "Campaign structure built around intent and offer clarity.",
          "Search + Performance Max (when appropriate).",
          "Conversion tracking setup (forms, calls, key events).",
          "Ad copy testing and keyword refinement.",
          "Landing page alignment to improve conversion rate.",
          "Weekly optimization + reporting with clear action steps.",
        ],
        outcomes: ["Faster leads", "Lower CPL", "Clear reporting"],
        testimonials: [
          {
            name: "Samir Patel",
            feedback:
              "We started getting qualified leads within weeks, and CPL improved steadily after optimizations.",
          },
          {
            name: "Laura Chen",
            feedback:
              "Reporting is clear. We know what’s working and what to change—no confusion anymore.",
          },
        ],
        faqs: [
          {
            question: "Do you manage Google Ads only or others too?",
            answer:
              "Google Ads is the primary channel. We can also manage Bing Ads and coordinate with other paid channels if needed.",
          },
          {
            question: "Do you need a minimum budget?",
            answer:
              "We recommend a budget that allows meaningful testing, but we can start lean and scale based on results.",
          },
          {
            question: "Will you create landing pages for ads?",
            answer:
              "Yes. We can build dedicated pages or improve your current pages to increase conversion rate.",
          },
        ],
      },

      {
        id: "local-business-growth",
        kicker: "Local",
        title: "Local SEO",
        description:
          "Improve visibility on maps and local search so customers near you find you first.",
        details:
          "Local SEO helps customers discover you at the exact moment they’re ready to buy. We optimize your Google Business Profile, improve your local landing pages, and strengthen citations and trust signals so you rank higher on Google Maps and local search.",
        features: [
          "Google Business Profile optimization (categories, services, photos).",
          "Local keyword strategy + service area targeting.",
          "Citation cleanup + consistent NAP signals (name/address/phone).",
          "Local landing page improvements for conversions.",
          "Review strategy guidance (ethical + practical).",
          "Tracking for calls, direction clicks, and form leads.",
        ],
        outcomes: ["More map calls", "Better local rank", "Trusted presence"],
        testimonials: [
          {
            name: "Priya Desai",
            feedback:
              "We started appearing in local map results more often and calls increased within the first month.",
          },
          {
            name: "Connor Hayes",
            feedback:
              "The profile cleanup and citations made a big difference. Visibility improved steadily.",
          },
        ],
        faqs: [
          {
            question: "Do you also help with multiple locations?",
            answer:
              "Yes. We can optimize multiple locations with a scalable structure and reporting.",
          },
          {
            question: "Is local SEO only for one city?",
            answer:
              "No. It works for service areas too. We can target neighborhoods, regions, and multi-city coverage.",
          },
          {
            question: "Do you handle citations for us?",
            answer:
              "Yes. We audit existing citations, fix inconsistencies, and build new listings where needed.",
          },
        ],
      },
    ],
    []
  );

  const idx = serviceData.findIndex((s) => s.id === serviceId);
  const service = idx >= 0 ? serviceData[idx] : null;

  const prev = idx > 0 ? serviceData[idx - 1] : null;
  const next = idx >= 0 && idx < serviceData.length - 1 ? serviceData[idx + 1] : null;

  const fallbackPhoneCall = webinfo?.phonecall || "+918000000000";
  const fallbackPhone = webinfo?.phone || "+91 80000 00000";
  const fallbackEmail = webinfo?.email || "hello@example.com";
  const fallbackHours = webinfo?.hours || "Mon–Sat · 10:00–19:00 IST";

  if (!service) {
    return (
      <section className="spx" aria-label="Service not found">
        <div className="spx-wrap">
          <div className="spx-topbar">
            <button className="spx-back" onClick={() => navigate("/services")}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Services
            </button>
          </div>

          <header className="spx-hero">
            <p className="spx-kicker">Services</p>
            <h1>Service not found</h1>
            <p className="spx-sub">
              We couldn’t find that service. Please go back and choose a service.
            </p>
          </header>
        </div>
      </section>
    );
  }

  const icon = iconMap[service.id] || faCode;

  return (
    <section className="spx" aria-label="Service details">
      <div className="spx-wrap">
        {/* Top bar */}
        <div className="spx-topbar">
          <button className="spx-back" onClick={() => navigate("/services")}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Services
          </button>

          <div className="spx-nav">
            <button
              className="spx-navbtn"
              disabled={!prev}
              onClick={() => prev && navigate(`/services/${prev.id}`)}
              aria-label="Previous service"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>Prev</span>
            </button>

            <button
              className="spx-navbtn"
              disabled={!next}
              onClick={() => next && navigate(`/services/${next.id}`)}
              aria-label="Next service"
            >
              <span>Next</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        </div>

        {/* Hero header (matches your hero section style) */}
        <header className="spx-hero" aria-labelledby="service-title">
          <div className="spx-badge">
            <span className="spx-badgeIcon" aria-hidden="true">
              <FontAwesomeIcon icon={icon} />
            </span>
            <span className="spx-badgeText">{service.kicker}</span>
          </div>

          <h1 id="service-title">
            {service.title}
            <span className="spx-grad"> for real growth.</span>
          </h1>

          <p className="spx-sub">{service.description}</p>

          <div className="spx-heroRow" aria-hidden="true">
            {service.outcomes?.slice(0, 3).map((o) => (
              <span className="spx-pill" key={o}>
                {o}
              </span>
            ))}
          </div>

          <div className="spx-ctaRow">
            <a className="spx-btn spx-btnPrimary" href="/contact">
              Get a quote
            </a>
            <button className="spx-btn spx-btnGhost" onClick={() => navigate("/services")}>
              View all services
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="spx-grid">
          <div className="spx-left">
            <article className="spx-card">
              <h2>How {brandName} helps</h2>
              <p>{service.details}</p>
            </article>

            <article className="spx-card">
              <h2>What's included</h2>
              <div className="spx-features">
                {service.features.map((f, i) => (
                  <div className="spx-feature" key={i}>
                    <span className="spx-tick" aria-hidden="true" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </article>

            {service.faqs?.length > 0 && (
              <article className="spx-card spx-faqs">
                <h2>Common questions</h2>
                {service.faqs.map((qa, i) => (
                  <details key={i}>
                    <summary>{qa.question}</summary>
                    <p>{qa.answer}</p>
                  </details>
                ))}
              </article>
            )}
          </div>

          {/* Sidebar */}
          <aside className="spx-right" aria-label="Contact">
            <div className="spx-side">
              <p className="spx-sideKicker">Talk to us</p>
              <h3>Get a free consultation</h3>
              <p className="spx-sideCopy">
                Share your goal and current setup. We'll suggest the best next step with a clear plan.
              </p>

              <div className="spx-meta">
                <div className="spx-metaRow">
                  <span className="spx-metaIcon" aria-hidden="true">
                    <FontAwesomeIcon icon={faPhone} />
                  </span>
                  <div>
                    <div className="spx-metaLabel">Call</div>
                    <a className="spx-metaValue" href={`tel:${fallbackPhoneCall}`}>
                      {fallbackPhone}
                    </a>
                  </div>
                </div>

                <div className="spx-metaRow">
                  <span className="spx-metaIcon" aria-hidden="true">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </span>
                  <div>
                    <div className="spx-metaLabel">Email</div>
                    <a className="spx-metaValue" href={`mailto:${fallbackEmail}`}>
                      {fallbackEmail}
                    </a>
                  </div>
                </div>

                <div className="spx-metaRow">
                  <span className="spx-metaIcon" aria-hidden="true">
                    <FontAwesomeIcon icon={faClock} />
                  </span>
                  <div>
                    <div className="spx-metaLabel">Hours</div>
                    <div className="spx-metaValue">{fallbackHours}</div>
                  </div>
                </div>
              </div>

              <div className="spx-sidePills" aria-hidden="true">
                <span>Fast response</span>
                <span>Clear scope</span>
                <span>Weekly updates</span>
                <span>No confusion</span>
              </div>

              <button
                className="spx-callBtn"
                onClick={() => (window.location.href = `tel:${fallbackPhoneCall}`)}
              >
                Call now
              </button>
            </div>
          </aside>
        </div>

        {/* Testimonials */}
        {service.testimonials?.length ? (
          <div className="spx-testimonials" aria-label="Testimonials">
            <h2>What clients say about us</h2>
            <div className="spx-tgrid">
              {service.testimonials.map((t, i) => (
                <div className="spx-quote" key={i}>
                  <p>“{t.feedback}”</p>
                  <span>— {t.name}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ServicesPage;
